import React, { useContext, useEffect, useState, useMemo } from "react";
import axios from "axios";
import { AuthContext } from "../../context/AuthContext";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Cell, PieChart, Pie, Legend } from "recharts";
import { motion } from "framer-motion";

const COLORS = ["#3b82f6", "#ef4444", "#f59e0b", "#10b981", "#8b5cf6", "#ec4899"];

const ContractAnalytics = () => {
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { authState } = useContext(AuthContext);

  useEffect(() => {
    const fetchContracts = async () => {
      try {
        setLoading(true);
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/bulk/contracts`,
          { 
            headers: { "x-auth-token": authState.token }
          }
        );
        setContracts(response.data);
      } catch (error) {
        console.error("Error fetching contracts:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchContracts();
  }, [authState.token]);

  const insights = useMemo(() => {
    if (contracts.length === 0) return null;

    const categoryValues = contracts.reduce((acc, contract) => {
      acc[contract.category] = (acc[contract.category] || 0) + parseInt(contract.contractAmount || 0);
      return acc;
    }, {});

    const topCategories = Object.entries(categoryValues)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([name, value], index) => ({ name, value, fill: COLORS[index % COLORS.length] }));

    const statusCounts = contracts.reduce((acc, contract) => {
      acc[contract.status] = (acc[contract.status] || 0) + 1;
      return acc;
    }, {});

    const statusData = Object.entries(statusCounts)
      .map(([name, value], index) => ({ name, value, fill: COLORS[index % COLORS.length] }));

    const averageContractValue = contracts.reduce((sum, contract) => sum + parseInt(contract.contractAmount || 0), 0) / contracts.length;

    const nearingExpiration = contracts.filter(contract => {
      const endDate = new Date(contract.endDate);
      const now = new Date();
      const daysUntilExpiration = (endDate - now) / (1000 * 60 * 60 * 24);
      return daysUntilExpiration <= 30 && daysUntilExpiration > 0;
    }).length;

    const totalValue = contracts.reduce((sum, contract) => sum + parseInt(contract.contractAmount || 0), 0);

    return {
      topCategories,
      statusData,
      averageContractValue,
      nearingExpiration,
      totalContracts: contracts.length,
      totalValue,
    };
  }, [contracts]);

  const formatCurrency = (value) => {
    return `Ksh ${value.toLocaleString()}`;
  };

  if (loading || !insights) {
    return <div className="text-center py-4">Loading...</div>;
  }

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-lg text-xs"
    >
      <h2 className="text-sm font-bold mb-3 text-gray-800 dark:text-white">Contract Insights</h2>
      
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
        <div>
          <p className="text-gray-600 dark:text-gray-400">Total Contracts</p>
          <p className="text-lg font-semibold text-blue-600 dark:text-blue-400">
            {insights.totalContracts}
          </p>
        </div>
        <div>
          <p className="text-gray-600 dark:text-gray-400">Total Value</p>
          <p className="text-lg font-semibold text-green-600 dark:text-green-400">
            {formatCurrency(insights.totalValue)}
          </p>
        </div>
        <div>
          <p className="text-gray-600 dark:text-gray-400">Avg Contract Value</p>
          <p className="text-lg font-semibold text-indigo-600 dark:text-indigo-400">
            {formatCurrency(Math.round(insights.averageContractValue))}
          </p>
        </div>
        <div>
          <p className="text-gray-600 dark:text-gray-400">Nearing Expiration</p>
          <p className="text-lg font-semibold text-yellow-600 dark:text-yellow-400">
            {insights.nearingExpiration}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <h3 className="text-xs font-semibold mb-2 text-gray-700 dark:text-gray-300">Top 5 Categories by Value</h3>
          <div className="h-32">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={insights.topCategories} layout="vertical" margin={{ left: 80, right: 20, top: 0, bottom: 0 }}>
                <XAxis type="number" hide />
                <YAxis type="category" dataKey="name" width={80} tick={{ fontSize: 9 }} />
                <Tooltip
                  contentStyle={{ fontSize: '10px' }}
                  formatter={(value) => [formatCurrency(value), 'Value']}
                />
                <Bar dataKey="value">
                  {insights.topCategories.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div>
          <h3 className="text-xs font-semibold mb-2 text-gray-700 dark:text-gray-300">Contract Status Distribution</h3>
          <div className="h-40">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={insights.statusData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={40}
                  fill="#8884d8"
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  labelLine={false}
                >
                  {insights.statusData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.fill} />
                  ))}
                </Pie>
                <Tooltip contentStyle={{ fontSize: '10px' }} />
                <Legend layout="vertical" align="right" verticalAlign="middle" />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default ContractAnalytics;